import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronLeft, Bell } from "lucide-react";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";

const TopHeader = ({ title, showBack = true, onBack }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  // Don't show back button on main pages
  const mainPages = ['/dashboard', '/'];
  const isMainPage = mainPages.includes(location.pathname);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Failed to fetch notification count:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const handleOpenNotifications = async (open) => {
    setNotifOpen(open);
    if (open) {
      await fetchNotifications();
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      try {
        await axios.put(`${API}/notifications/${notif.notification_id}/read`, {}, { withCredentials: true });
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => 
          n.notification_id === notif.notification_id ? { ...n, is_read: true } : n
        ));
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
    
    // Navigate to link
    if (notif.link) {
      setNotifOpen(false);
      navigate(notif.link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-cream/95 backdrop-blur-lg border-b border-sage/10">
      <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          {showBack && !isMainPage && (
            <button 
              onClick={handleBack} 
              className="p-2 -ml-2 rounded-full hover:bg-sage/10 transition-colors"
              aria-label="Go back"
            >
              <ChevronLeft className="w-5 h-5 text-sage-darker" />
            </button>
          )}
          {title ? (
            <h1 className="text-lg font-serif text-sage-darker">{title}</h1>
          ) : (
            <Link to="/dashboard" className="flex items-center gap-2">
              <span className="text-xl font-serif text-sage-dark tracking-wider">WOMBS</span>
            </Link>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Notifications Bell */}
          <Popover open={notifOpen} onOpenChange={handleOpenNotifications}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5 text-sage-dark" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-white" align="end">
              <div className="flex items-center justify-between px-4 py-3 border-b border-sage/10">
                <h3 className="font-medium text-sage-darker">Notifications</h3>
                {unreadCount > 0 && (
                  <button 
                    onClick={handleMarkAllRead}
                    className="text-xs text-sage-dark hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    No notifications yet
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.notification_id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`px-4 py-3 border-b border-sage/5 cursor-pointer hover:bg-sage/5 transition-colors ${
                        !notif.is_read ? 'bg-sage/10' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0">
                          {notif.actor_picture ? (
                            <img src={notif.actor_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <Bell className="w-4 h-4 text-sage-dark" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-sage-darker font-medium truncate">{notif.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                          </p>
                        </div>
                        {!notif.is_read && (
                          <div className="w-2 h-2 bg-sage rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Profile Avatar */}
          <Link to="/profile" data-testid="profile-button">
            <Avatar className="h-9 w-9 border-2 border-sage/30 hover:border-sage transition-colors">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-sage/20 text-sage-dark text-sm">
                {user?.name?.[0]}
              </AvatarFallback>
            </Avatar>
          </Link>
        </div>
      </div>
    </header>
  );
};

export default TopHeader;
